import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { DeficiencyView } from "../../../components/deficiency-view.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { useAuthGuard } from "../../../hooks/useAuthGuard";
import { Roles } from "../../../types/user.types";
import HeaderNavBar from "../../../components/header-nav-bar";
import { useDeficiencyReviewService } from "../../../services/deficiency-review.service";
import { DeficiencyReview } from "../../../types/deficiency.types";

export default function ReviewDeficiency() {
  useAuthGuard(Roles.Admin);

  const navigate = useNavigate();
  const location = useLocation();
  const { matterId, deficiencyId } = useParams();
  const [backButtonText, setBackButtonText] = useState<string>("Back to Matter");
  const [backButtonUrl, setBackButtonUrl] = useState<string>(`/admin/matters/${matterId}/details`);
  const [deficiencyReview, setDeficiencyReview] = useState<DeficiencyReview | null>();
  const [isOnBreak, setIsOnBreak] = useState<boolean>(false);
  const { getNextReviewableDeficiencyByMatter, postDeficiencyReviewBreak } = useDeficiencyReviewService();

  useEffect(() => {
    document.documentElement.style.backgroundColor = "white";
    return () => {
      document.documentElement.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    let text = "Back to Matter";
    let url = `/admin/matters/${matterId}/details`;
    if (location.state?.from) {
      url = location.state?.from;
      if (location.state?.from.includes("deficiencies")) {
        text = "Back to Deficiencies";
      }
    }
    setBackButtonText(text);
    setBackButtonUrl(url);
  }, [location.state?.from, matterId]);

  const getNextDeficiencyReview = useCallback(() => {
    if (!matterId) {
      return;
    }
    getNextReviewableDeficiencyByMatter(matterId)
      .then((deficiencyReview) => {
        if (!deficiencyId && deficiencyReview) {
          navigate(`/admin/matters/${matterId}/deficiencies/review/${deficiencyReview.deficiencyId}`);
        } else {
          setDeficiencyReview(deficiencyReview);
        }
      })
      .catch(() => setDeficiencyReview(null));
  }, [deficiencyId, getNextReviewableDeficiencyByMatter, matterId, navigate]);

  useEffect(() => {
    if (!matterId) return;

    if (!deficiencyId && !isOnBreak) {
      getNextDeficiencyReview();
    }
  }, [deficiencyId, getNextDeficiencyReview, matterId, isOnBreak]);

  const handlePaginationNavigate = useCallback(
    (deficiencyId: number) => {
      navigate(`/admin/matters/${matterId}/deficiencies/review/${deficiencyId}`);
    },
    [matterId, navigate]
  );

  const handleFinish = useCallback(() => {
    navigate(backButtonUrl);
  }, [backButtonUrl, navigate]);

  const handleTakeBreak = useCallback(() => {
    if (deficiencyId) {
      // Taking a break - call the API and show break screen
      postDeficiencyReviewBreak(deficiencyId).then(() => {
        setIsOnBreak(true);
      }).catch((error) => {
        console.error("Failed to take break:", error);
      });
    }
  }, [deficiencyId, postDeficiencyReviewBreak]);

  const handleResume = useCallback(() => {
    // Resuming - get next available deficiency review
    setIsOnBreak(false);
    if (!matterId) return;
    
    getNextReviewableDeficiencyByMatter(matterId)
      .then((deficiencyReview) => {
        if (deficiencyReview) {
          navigate(`/admin/matters/${matterId}/deficiencies/review/${deficiencyReview.deficiencyId}`);
        } else {
          handleFinish();
        }
      })
      .catch(() => handleFinish());
  }, [matterId, getNextReviewableDeficiencyByMatter, navigate, handleFinish]);

  const handleWorkLoaded = useCallback(() => {
    // Empty callback to avoid creating new function on each render
  }, []);

  if (!matterId || !deficiencyId) {
    return (
      <div className="container-fluid bg-sw-secondary">
        <div className="row">
          <div className="col text-center mt-5">
            <h1>ALL DONE</h1>
            <div className="mb-5">No more deficiencies to review</div>
            <button className="btn btn-sw btn-outline" onClick={() => navigate(`/admin/matters/${matterId}/details`)}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-sw-secondary">
      <HeaderNavBar
        variant="container-fluid"
        leftContent={
          <Link to={backButtonUrl} className="btn btn-sw btn-sm btn-outline me-3">
            <FontAwesomeIcon icon={faCircleLeft} className="me-2" />
            {backButtonText}
          </Link>
        }
      />

      <div className="bg-sw-secondary pt-4">
        {/*
        <Pagination sheetNumbers={deficiencyIds} onNavigate={handlePaginationNavigate} />
        */}
        {!!deficiencyId && (
          <DeficiencyView
            key={deficiencyId}
            matterId={matterId}
            deficiencyId={deficiencyId}
            getNextDeficiencyReview={getNextDeficiencyReview}
            onNavigate={handlePaginationNavigate}
            onFinished={handleFinish}
            onWorkLoaded={handleWorkLoaded}
            onTakeBreak={isOnBreak ? handleResume : handleTakeBreak}
            isOnBreak={isOnBreak}
          />
        )}
      </div>
    </div>
  );
}
